// Scenario Management Service - Single Source of Truth for all calculators
// Handles saving, loading, and exporting scenarios across Basic, Advanced, and Accumulation calculators

// ASIC-compliant disclaimer text
export const ASIC_DISCLAIMER = `IMPORTANT DISCLAIMER

This calculator is for educational and informational purposes only. It is not intended to be, and should not be considered as, financial advice or a recommendation to purchase any financial product.

The calculations and projections provided are based on assumptions and estimates that may not reflect actual market conditions or future performance. Investment returns, inflation rates, and other economic factors can vary significantly and may differ from the assumptions used in this calculator.

Before making any financial decisions, you should:
• Seek advice from a qualified financial adviser
• Consider your personal financial situation, objectives, and needs
• Obtain and read the relevant Product Disclosure Statement (PDS)
• Consider the risks associated with any investment

Past performance is not a reliable indicator of future performance. The value of investments can go up or down, and you may receive back less than you invested.

This calculator does not take into account your personal circumstances, financial situation, or needs. It should not be relied upon as the sole basis for any financial decision.

By using this calculator, you acknowledge that you understand these limitations and agree to use the information provided at your own risk.`;

// Create a standardized scenario template
export const createScenarioTemplate = (calculatorType, inputs, results = null) => {
  return {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    name: '',
    calculatorType: calculatorType, // 'basic', 'advanced', 'accumulation'
    createdAt: new Date().toISOString(),
    inputs: { ...inputs },
    results: results ? { ...results } : null
  };
};

// Centralized scenario management service
export class ScenarioService {
  static STORAGE_KEY = 'superannuation_scenarios';
  static DISCLAIMER_KEY = 'asic_disclaimer_accepted';

  // Save scenario to localStorage
  static saveScenario(scenario) {
    try {
      const scenarios = this.getAllScenarios();
      const existingIndex = scenarios.findIndex(s => s.id === scenario.id);
      if (existingIndex >= 0) {
        scenarios[existingIndex] = scenario;
        } else {
        scenarios.push(scenario);
        }
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(scenarios));
      return true;
    } catch (error) {
      return false;
    }
  }

  // Load scenario from localStorage
  static loadScenario(scenarioId) {
    try {
      const scenarios = this.getAllScenarios();
      return scenarios.find(s => s.id === scenarioId) || null;
    } catch (error) {
      return null;
    }
  }

  // Delete scenario from localStorage
  static deleteScenario(scenarioId) {
    try {
      const scenarios = this.getAllScenarios();
      const filteredScenarios = scenarios.filter(s => s.id !== scenarioId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredScenarios));
      return true;
    } catch (error) {
      return false;
    }
  }

  // Get all scenarios
  static getAllScenarios() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  }

  // Get scenarios for specific calculator
  static getScenariosForCalculator(calculatorType) {
    const allScenarios = this.getAllScenarios();
    // Migrate scenarios that don't have createdAt
    const migratedScenarios = allScenarios.map(scenario => {
      if (!scenario.createdAt) {
        scenario.createdAt = new Date().toISOString();
      }
      return scenario;
    });
    
    // Save migrated scenarios back to localStorage if any were updated
    if (migratedScenarios.some((scenario, index) => !allScenarios[index].createdAt)) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(migratedScenarios));
    }
    
    const filtered = migratedScenarios.filter(s => s.calculatorType === calculatorType);
    return filtered;
  }

  // Export scenario to JSON
  static exportScenarioToJSON(scenario) {
    const dataStr = JSON.stringify(scenario, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${scenario.name || 'scenario'}_${scenario.calculatorType}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Export results to CSV
  static exportResultsToCSV(scenario) {
    if (!scenario.results) {
      alert('No results to export');
      return;
    }

    let csvContent = '';
    
    // Add scenario info
    csvContent += `Scenario Name,${scenario.name}\n`;
    csvContent += `Calculator Type,${scenario.calculatorType}\n`;
    csvContent += `Created At,${scenario.createdAt ? new Date(scenario.createdAt).toLocaleDateString() : 'Unknown date'}\n\n`;

    // Add results based on calculator type
    if (scenario.calculatorType === 'basic') {
      csvContent += `Maximum Sustainable Income,${scenario.results.maxSustainableIncome || 0}\n`;
      csvContent += `Final Balance,${scenario.results.finalBalance || 0}\n`;
    } else if (scenario.calculatorType === 'advanced') {
      csvContent += `Sustainable Income,${scenario.results.sustainableIncome || 0}\n`;
      csvContent += `Final Balance,${scenario.results.finalBalance || 0}\n`;
    } else if (scenario.calculatorType === 'accumulation') {
      csvContent += `Final Balance Median,${scenario.results.finalBalances?.median || 0}\n`;
      csvContent += `Final Balance Best Case,${scenario.results.finalBalances?.bestCase || 0}\n`;
      csvContent += `Final Balance Worst Case,${scenario.results.finalBalances?.worstCase || 0}\n`;
    }

    const dataBlob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${scenario.name || 'scenario'}_results.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Export summary to PDF (basic implementation)
  static exportSummaryToPDF(scenario) {
    // For now, create a printable HTML summary
    const printWindow = window.open('', '_blank');
    const html = `
      <html>
        <head>
          <title>${scenario.name || 'Scenario'} Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #1f2937; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
            .section { margin: 20px 0; }
            .label { font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Superannuation Calculator - ${scenario.name || 'Scenario'}</h1>
          <div class="section">
            <div class="label">Calculator Type:</div>
            <div>${scenario.calculatorType.charAt(0).toUpperCase() + scenario.calculatorType.slice(1)}</div>
          </div>
          <div class="section">
            <div class="label">Created:</div>
            <div>${scenario.createdAt ? new Date(scenario.createdAt).toLocaleDateString() : 'Unknown date'}</div>
          </div>
          <div class="section">
            <div class="label">Key Results:</div>
            <div>
              ${scenario.calculatorType === 'basic' ? `
                Maximum Sustainable Income: $${(scenario.results?.maxSustainableIncome || 0).toLocaleString()}/year<br>
                Final Balance: $${(scenario.results?.finalBalance || 0).toLocaleString()}
              ` : scenario.calculatorType === 'advanced' ? `
                Sustainable Income: $${(scenario.results?.sustainableIncome || 0).toLocaleString()}/year<br>
                Final Balance: $${(scenario.results?.finalBalance || 0).toLocaleString()}
              ` : `
                Final Balance (Median): $${(scenario.results?.finalBalances?.median || 0).toLocaleString()}<br>
                Final Balance (Best): $${(scenario.results?.finalBalances?.bestCase || 0).toLocaleString()}<br>
                Final Balance (Worst): $${(scenario.results?.finalBalances?.worstCase || 0).toLocaleString()}
              `}
            </div>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  }

  // Disclaimer management
  static hasAcceptedDisclaimer() {
    try {
      return localStorage.getItem(this.DISCLAIMER_KEY) === 'true';
    } catch (error) {
      return false;
    }
  }

  static acceptDisclaimer() {
    try {
      localStorage.setItem(this.DISCLAIMER_KEY, 'true');
      return true;
    } catch (error) {
      return false;
    }
  }
}

export default ScenarioService;
